/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React components
import MasterCard from "./components/MasterCard";
import DefaultInfoCard from "components/patterns/DefaultInfoCard";
import { useMaterialUIController } from "context";
import { useApiClient } from "common/ApiClientProvider";
import { useStripe, useElements, Elements, PaymentElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe("pk_test_KtVhdGkKsR83L222KaGaDrtT");

// Billing page components
import BaseLayout from "../components/BaseLayout";
import BillingInformation from "./components/BillingInformation";
import Transactions from "./components/Transactions";
import CardInfoForm from "./components/CardInfoForm";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import masterCardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import jcbLogo from "assets/images/logos/jcb.png";
import dinersClubLogo from "assets/images/logos/dinersclub.png";
import americanExpressLogo from "assets/images/logos/americanexpress.png";

function Billing() {
  const { t } = useTranslation("pages");
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { getUser } = useKindeAuth();
  const apiClient = useApiClient();
  const [clientSecret, setClientSecret] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState("");
  const [cards, setCards] = useState([]);
  const handleSetupOpen = () => {
    getClientSecret();
    setSetupOpen(true);
  };
  const handleSetupClose = () => setSetupOpen(false);

  const options = {
    // passing the SetupIntent's client secret
    clientSecret: clientSecret,
    // Fully customizable with appearance API.
    // appearance: {/*...*/},
  };
  const user = getUser();
  async function getClientSecret() {
    const customer = await apiClient.post("current_customer", {
      body: { email: user.email },
    });
    const setupIntent = await apiClient.post("setup_intents/", {
      body: { customer_id: customer.id },
    });
    setSetupIntentId(setupIntent.id);
    setClientSecret(setupIntent.client_secret);
  }

  async function cancelSetupIntent() {
    await apiClient.delete(`setup_intents/${encodeURIComponent(setupIntentId)}/`, {
      body: { cancellation_reason: "abandoned" },
    });
  }
  async function getSetupIntents() {
    await apiClient.get("setup_intents/");
  }

  async function getPaymentMethods() {
    const customer = await apiClient.post("current_customer", {
      body: { email: user.email },
    });
    const paymentMethods = await apiClient.get("payment_methods/", {
      query: { customer_id: customer.id },
    });
    setCards(paymentMethods.data);
  }
  async function deletePaymentMethod(id) {
    await apiClient.delete(`payment_methods/${encodeURIComponent(id)}/`);
    await getPaymentMethods();
  }
  useEffect(() => {
    getPaymentMethods();
  }, []);
  return (
    <BaseLayout stickyNavbar>
      <Modal
        open={setupOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "scroll",
        }}
      >
        {clientSecret ? (
          <Elements stripe={stripePromise} options={options}>
            <CardInfoForm
              handleSetupClose={handleSetupClose}
              cancelSetupIntent={cancelSetupIntent}
              getPaymentMethods={getPaymentMethods}
            />
          </Elements>
        ) : (
          <CircularProgress color="inherit" />
        )}
      </Modal>
      <MDBox mt={4}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card id="delete-account">
                <MDBox
                  pt={2}
                  px={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" fontWeight="medium">
                    {t("account.paymentMethod")}
                  </MDTypography>
                  <MDButton variant="gradient" color="dark" onClick={() => handleSetupOpen()}>
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;add new card
                  </MDButton>
                </MDBox>
                <MDBox p={2}>
                  <Grid container spacing={3}>
                    {cards.map((item, index) => (
                      <Grid item xs={12} md={12} key={item.id}>
                        <MDBox
                          borderRadius="lg"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          p={3}
                          sx={{
                            border: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          }}
                        >
                          <MDBox
                            component="img"
                            src={
                              item.card.brand === "mastercard"
                                ? masterCardLogo
                                : item.card.brand === "visa"
                                ? visaLogo
                                : item.card.brand === "diners"
                                ? dinersClubLogo
                                : item.card.brand === "amex"
                                ? americanExpressLogo
                                : item.card.brand === "jcb"
                                ? jcbLogo
                                : ""
                            }
                            width="10%"
                            mr={2}
                          />
                          <MDTypography variant="h6" fontWeight="medium">
                            ****&nbsp;&nbsp;****&nbsp;&nbsp;****&nbsp;&nbsp;{item.card.last4}
                          </MDTypography>
                          <MDBox
                            ml="auto"
                            lineHeight={0}
                            color={darkMode ? "white" : "dark"}
                            onClick={() => {
                              deletePaymentMethod(item.id);
                            }}
                          >
                            <Tooltip title={t("account.deleteCard")} placement="top">
                              <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                delete
                              </Icon>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <BillingInformation />
            </Grid>
          </Grid>
        </MDBox>
        {/*}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={12}>
              <Invoices />
            </Grid>
          </Grid>
        </MDBox>
        {*/}
      </MDBox>
    </BaseLayout>
  );
}

export default Billing;
